import { createApp } from 'vue'
import { createPinia, type Pinia } from 'pinia'
import App from '@/App.vue'
import router from '@/router/router'
import { AuthenticationService } from '@/services/AuthenticationService'
import { useUserStore } from '@/stores/user'
import { useMedicalFieldsStore } from '@/stores/medicalFields'
import i18n, { changeLanguage, type languages } from '@/i18n/i18n'
import vuetify from '@/plugins/vuetify'
import { UserSettingsService } from '@/services/telehealthApi/UserSettingsService'
import './index.css'
import * as Sentry from '@sentry/vue'
import { getSentryDsn, getSentryEnvironment } from './global/environmentGetters'
import { MaintenanceGuardService } from '@/services/MaintenanceGuardService'
import { startTelemetry } from '@/telemetry/telemetry'
import { PluginService } from './services/telehealthApi/PluginService'
import type { UserSettingsUpdateDTO } from '@/generated'

const pinia: Pinia = createPinia()
const userStore = useUserStore(pinia)

AuthenticationService.initializeKeycloak()
;(async () => {
  const isAlreadyLoggedIn = await AuthenticationService.isAlreadyLoggedIn()

  if (!isAlreadyLoggedIn) {
    userStore.setUser(undefined)
    const displayMaintenancePage = MaintenanceGuardService.shouldDisplayMaintenancePage()
    if (displayMaintenancePage) {
      MaintenanceGuardService.showMaintenance()
    } else {
      await AuthenticationService.loginUser()
      await initializeApp()
    }
  } else {
    await initializeApp()
  }
})()

async function setKeycloakLocale(): Promise<void> {
  try {
    const userLanguage = userStore.user?.language
    const keycloakProfile = await AuthenticationService.getKeycloakProfile()
    const keycloakLocaleList = keycloakProfile?.attributes?.locale as string[]
    const chosenKeycloakLanguage = keycloakLocaleList[0]

    if (chosenKeycloakLanguage !== userLanguage) {
      const userUpdateDTO: UserSettingsUpdateDTO = {
        language: {
          value: chosenKeycloakLanguage
        }
      }

      await UserSettingsService.updateUserSettings(userStore.user!.userName, userUpdateDTO)
      userStore.setLanguage(chosenKeycloakLanguage as languages)
      changeLanguage(chosenKeycloakLanguage as languages)
    }
  } catch (e) {
    console.error('Failed updating locale:', (e as Error).message)
  }
}

async function initializeApp() {
  await AuthenticationService.initializeApp()
  await UserSettingsService.syncUserWithBackend()
  await PluginService.syncPluginsWithBackend()
  await setKeycloakLocale()
  try {
    await useMedicalFieldsStore(pinia).load()
  } catch (e) {
    console.error('Failed to get medical fields translations')
  }
  const app = createApp(App)

  const sentryDsn = getSentryDsn()
  const sentryEnvironment = getSentryEnvironment()

  if (sentryDsn && sentryEnvironment) {
    console.log('Sentry initialized')
    Sentry.init({
      app,
      dsn: sentryDsn,
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      environment: sentryEnvironment
    })
  }

  startTelemetry(router)

  app.use(router)
  app.use(i18n)
  app.use(vuetify)
  app.use(pinia)
  app.mount('#app')
  UserSettingsService.loadAssignedHospitalsAndWards()
}
